import { Box, Text } from "@chakra-ui/react";
import { Attribute, mergeAttributes, Node } from "@tiptap/core";
import { NodeViewWrapper, ReactNodeViewRenderer } from "@tiptap/react";
import { DateFormatType } from "interfaces/models/inspectionItemResult";
import {
  DEFAULT_PIN_COMPONENT_STYLE,
  PinComponentStyle,
} from "interfaces/models/pinComponent";

import { useRef, useState } from "react";
import { CustomNodeComponentProps, NodeType } from "../type";
import { dispatchUpdateContentEvent, handleCommandForNode } from "../utils";
import DatePicker, { DatePickerRef } from "components/DatePicker";

export interface DateNodeAttrs {
  date: Date | null;
  formatType: DateFormatType;
  style?: PinComponentStyle;
}

export const DateNode = Node.create({
  name: NodeType.DATE,
  group: "block",
  selectable: false,

  addAttributes() {
    const attrs: {
      [key in keyof DateNodeAttrs]: Attribute;
    } = {
      date: { default: undefined },
      style: { default: DEFAULT_PIN_COMPONENT_STYLE },
      formatType: { default: DateFormatType.M_D },
    };

    return attrs;
  },
  parseHTML() {
    return [
      {
        tag: "datetime",
      },
    ];
  },
  renderHTML({ HTMLAttributes }) {
    return ["datetime", mergeAttributes(HTMLAttributes)];
  },

  addNodeView() {
    return ReactNodeViewRenderer(Component);
  },
});

const Component = (props: CustomNodeComponentProps<DateNodeAttrs>) => {
  const { formatType, date, style } = props.node.attrs;
  const editorAttributes = props.editor.options.editorProps?.attributes as any;
  const isPreview = editorAttributes?.isPreview === "true";
  const [isValidDate, onChangeStatusValidDate] = useState<boolean>(true);
  const datePickerRef = useRef<DatePickerRef | null>(null);
  const [isShowCalendar, setIsShowCalendar] = useState<boolean>(false);

  const onChange = async (updateDate: Date) => {
    props.updateAttributes({ date: updateDate, formatType });
    // update pin component node
    const pos = props.getPos() - 1;
    const node = props.editor.state.doc.nodeAt(pos);
    if (!node || isPreview) {
      return;
    }

    handleCommandForNode({
      isFocusEditor: false,
      editor: props.editor,
      command: ({ tr }) => {
        tr.setNodeMarkup(pos, undefined, {
          ...node.attrs,
          isUpdated: true,
          updatedAt: new Date(),
        });

        return true;
      },
    });

    dispatchUpdateContentEvent(props.editor);

    return;
  };

  return (
    <NodeViewWrapper
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: style?.alignItems,
        cursor: "default",
        pointerEvents: "auto",
        position: "relative",
      }}
      className={`wrapper-node-date ${isValidDate ? "" : "has-error"} ${
        isShowCalendar ? "wrapper-node-date-focusing" : ""
      }`}
    >
      <Box
        style={{
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: style?.alignItems,
        }}
        onClick={() => {
          if (isPreview) {
            return;
          }
          datePickerRef?.current?.focusInput();
        }}
      >
        {!isValidDate && (
          <Text fontSize="1.4rem" className="tooltip-text">
            日付形式が無効です
          </Text>
        )}
        <Box width="100%" height="fit-content">
          <DatePicker
            ref={datePickerRef}
            isReadOnly={isPreview}
            value={date}
            showIcon={false}
            isShowTooltip={false}
            formatType={formatType}
            isShowCalendar={isShowCalendar}
            setIsShowCalendar={setIsShowCalendar}
            onChangeStatusValidDate={onChangeStatusValidDate}
            inputStyle={{
              minHeight: "1em",
              textAlign: style?.textAlign,
              fontSize: `${style?.fontSize}px`,
              width: "100%",
              paddingLeft: 0,
              height: "initial",
              backgroundColor: "transparent",
              border: "none",
              boxShadow: "none",
              ...(isPreview ? { paddingRight: 0 } : {}),
            }}
            onChange={onChange as any}
          />
        </Box>
      </Box>
    </NodeViewWrapper>
  );
};
